import { PlusIcon } from '@heroicons/react/20/solid'
import {
	ChevronRightIcon,
	InformationCircleIcon,
	MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import { yupResolver } from '@hookform/resolvers/yup'
import { ColumnDef } from '@tanstack/react-table'
import {
	ArrowDown,
	ArrowUp,
	Check,
	ChevronDownIcon,
	ChevronsUpDown,
	Eye,
	Loader2,
	MessageCircleMoreIcon,
	MessageCirclePlusIcon,
} from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from 'react-query'

import { format, isAfter, isSameDay, parseISO, subDays } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { enUS } from 'date-fns/locale'
import { toast } from 'sonner'
import { useMediaQuery } from 'usehooks-ts'
import * as yup from 'yup'

import { EditIcon, TaskSquareIcon } from '@/assets/icons'
import {
	SalesRecordDialogState,
	SalesRecordsDialog,
	SalesRecordsHistoryDialog,
} from '@/components/sales-records-dialog'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command'
import { DataTable, DataTableColumnHeader } from '@/components/ui/data-table'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PaginationComponent } from '@/components/ui/pagination'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
	ResponsiveModal,
	ResponsiveModalContent,
	ResponsiveModalDescription,
	ResponsiveModalHeader,
	ResponsiveModalTitle,
} from '@/components/ui/responsive-dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { EXTRAS, generateRandomPassword, PACKAGES, primaryIbanOptions } from '@/constants'
import { capitalizeFirstLetter, formatIban, normalizeIban } from '@/lib/helper'
import { cn } from '@/lib/utils'
import { CreateBookingForm } from '@/pages/admin/bookings-table'
import authService from '@/services/auth-service'
import bookingService from '@/services/booking-service'
import userService from '@/services/user-service'
import { InputMask } from '@react-input/mask'

export const CollectionsTable = () => {
	const searchableColumns = [
		{ label: 'Buchungs-ID', value: 'id' },
		{ label: 'Regale-ID', value: 'stand_id' },
		{ label: 'User ID', value: 'user_id' },
		{ label: 'Dauer', value: 'duration' },
		{ label: 'Paket', value: 'collect_product_by' },
		{ label: 'Preis', value: 'rent_price' },
		{ label: 'Status', value: 'collection_status' },
	]

	const isSmallScreen = useMediaQuery('(max-width: 64rem)')

	const [expandedRows, setExpandedRows] = useState<number[]>([])
	const [filterValue, setFilterValue] = useState('')
	const [isCreateDialogVisible, setIsCreateDialogVisible] = useState(false)
	const [isNoteDialogVisible, setIsNoteDialogVisible] = useState<{
		id: number | null
		note: string
		type: string | null
	}>({
		id: null,
		note: '',
		type: null,
	})
	const [isUserDetailDialogVisible, setIsUserDetailDialogVisible] = useState<User | null>(null)
	const [isSalesRecordDialogVisible, setIsSalesRecordDialogVisible] =
		useState<SalesRecordDialogState>(null)
	const [isSalesRecordHistoryDialogVisible, setIsSalesRecordHistoryDialogVisible] =
		useState<SalesRecordDialogState>(null)
	const [searchValue, setSearchValue] = useState('')
	const [selectedStatus, setSelectedStatus] = useState('all')
	const [page, setPage] = useState(1)
	const [sortColumn, setSortColumn] = useState('')
	const [sortDirection, setSortDirection] = useState('')
	const [sortType, setSortType] = useState<'asc' | 'desc' | null>(null)
	const [selectedColumn, setSelectedColumn] = useState('user_id')
	const [rowsPerPage, setRowsPerPage] = useState(50)
	const [openStartCalendar, setOpenStartCalendar] = useState(false)
	const [openEndCalendar, setOpenEndCalendar] = useState(false)
	const [open, setOpen] = useState(false)

	const schema = yup.object<CreateBookingForm>().shape({
		userType: yup.string().required(),
		existingUser: yup.string().when('userType', {
			is: 'existing',
			then: schema => schema.required('Benutzer ist erforderlich!'),
			otherwise: schema => schema.notRequired(),
		}),
		rent_price: yup
			.string()
			.test('rent-price-validation', 'Preis ist erforderlich!', function (value) {
				const pckg = this.parent.package
				if (pckg === 'custom' && !value) return false
				return true
			}),
		firstName: yup.string().when('userType', {
			is: 'new',
			then: schema => schema.required('Name ist erforderlich!'),
			otherwise: schema => schema.notRequired(),
		}),
		lastName: yup.string().when('userType', {
			is: 'new',
			then: schema => schema.required('Name ist erforderlich!'),
			otherwise: schema => schema.notRequired(),
		}),
		email: yup.string().when('userType', {
			is: 'new',
			then: schema =>
				schema
					.required('E-Mail ist erforderlich')
					.email('Geben Sie eine gültige E-Mail-Adresse ein'),
			otherwise: schema => schema.notRequired(),
		}),
		country: yup.string().when('userType', {
			is: 'new',
			then: schema => schema.required('Ort ist erforderlich!'),
			otherwise: schema => schema.notRequired(),
		}),
		zip: yup.string().when('userType', {
			is: 'new',
			then: schema => schema.required('Postleitzahl ist erforderlich!'),
			otherwise: schema => schema.notRequired(),
		}),
		iban: yup.string().when('userType', {
			is: 'new',
			then: schema =>
				schema
					.required('IBAN ist erforderlich!')
					.matches(/^DE(\s?\d){20}$/, "IBAN muss mit 'DE' beginnen und genau 20 Zahlen enthalten"),
			otherwise: schema => schema.notRequired(),
		}),
		address: yup.string().when('userType', {
			is: 'new',
			then: schema => schema.required('Adresse ist erforderlich!'),
			otherwise: schema => schema.notRequired(),
		}),
		phoneNumber: yup.string().when('userType', {
			is: 'new',
			then: schema => schema.required('Telefonnummer ist erforderlich!'),
			otherwise: schema => schema.notRequired(),
		}),
		package: yup.string().required('Paket ist erforderlich!'),
		start_date: yup.date().required('Startdatum ist erforderlich!'),
		end_date: yup.date().required('Enddatum ist erforderlich!'),
		shelf: yup.string().required('Regal Nummer ist erforderlich!'),
		additionalServices: yup.string().required('Zusatzleistungen ist erforderlich!'),
	})

	const form = useForm<CreateBookingForm>({
		resolver: yupResolver(schema as any),
		defaultValues: { userType: 'new' },
		mode: 'all',
	})

	const { data, refetch, isLoading } = useQuery(
		['fetch-collections', selectedStatus],
		() =>
			bookingService.fetchCollectionsData({
				pageNumber: page.toString(),
				pageSize: rowsPerPage.toString(),
				filterColumn: selectedColumn,
				filterValue,
				collectionStatus: selectedStatus,
			}),
		{
			refetchOnWindowFocus: false,
			onError: () => {
				toast.error('Error fetching collections')
			},
		},
	)

	const startDate = form.watch('start_date')
	const endDate = form.watch('end_date')

	const { data: users } = useQuery('fetch-users', () => userService.fetchAllUsers(), {
		refetchOnWindowFocus: false,
		enabled: form.watch('userType') === 'existing',
		onError: () => {
			toast.error('Error fetching users')
		},
	})

	const { data: holidays } = useQuery('fetch-holidays', () => bookingService.fetchHolidays(), {
		enabled: isCreateDialogVisible,
		refetchOnWindowFocus: false,
		onError: () => toast.error('Error fetching holidays'),
	})

	const { mutate, data: standList } = useMutation({
		mutationKey: 'fetch-stands',
		mutationFn: ({
			startDate,
			endDate,
		}: {
			startDate: Booking['start']
			endDate: Booking['end']
		}) => bookingService.fetchStandsData(startDate, endDate),
	})

	const { mutate: updateNote, isLoading: isUpdatingNote } = useMutation({
		mutationKey: 'update-note',
		mutationFn: ({
			note,
			id,
		}: {
			note: { user_notes: string } | { booking_notes: string }
			id: number
		}) => bookingService.updateNote(note, id),
		onSuccess: () => {
			toast.success('Hinweis erfolgreich aktualisiert')
			setIsNoteDialogVisible({ note: '', type: null, id: null })
			refetch()
		},
		onError: (err: any) => {
			toast.error(
				err?.response?.data?.message ??
					'Hinweis konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.',
			)
		},
	})

	const { mutate: registerUser } = useMutation({
		mutationKey: 'register-user',
		mutationFn: (data: any) => authService.registerUser(data),
	})

	const { mutate: placeOrder } = useMutation({
		mutationKey: 'place-order',
		mutationFn: (data: any) => bookingService.placeAdminOrder(data),
	})

	const { mutate: sendBookingEmail, isLoading: placingBooking } = useMutation({
		mutationKey: 'send-email',
		mutationFn: (data: any) => bookingService.sendBookingEmail(data),
	})

	const { mutate: verifyEmail } = useMutation({
		mutationKey: 'verify-email',
		mutationFn: (data: { email: string }) => authService.verifyEmail(data),
	})

	const { mutate: updateCollectionStatus } = useMutation({
		mutationKey: 'update-collection-status',
		mutationFn: (data: { id: number; status: string }) =>
			bookingService.updateCollectionStatus(data.id, data.status),
	})

	useEffect(() => {
		refetch()
	}, [rowsPerPage, page])

	useEffect(() => {
		if (startDate && endDate) {
			mutate({
				startDate: format(startDate, 'yyyy-MM-dd'),
				endDate: format(endDate, 'yyyy-MM-dd'),
			})
		}
	}, [startDate, endDate])

	const handleSalesRecordDialogOpen = (booking: Booking) => {
		setIsSalesRecordDialogVisible({
			bookingId: booking.id,
			userId: booking.user_id,
			userName: `${booking.user.first_name} ${booking.user.last_name}`.trim(),
		})
	}

	const handleSalesRecordHistoryDialogOpen = (booking: Booking) => {
		setIsSalesRecordHistoryDialogVisible({
			bookingId: booking.id,
			userId: booking.user_id,
			userName: `${booking.user.first_name} ${booking.user.last_name}`.trim(),
		})
	}

	const sortedData = useMemo(() => {
		if (!data) return []
		if (!sortColumn) return data.data

		return [...data.data].sort((a, b) => {
			const aValue = a[sortColumn as keyof Booking]
			const bValue = b[sortColumn as keyof Booking]

			if (aValue == null || bValue == null) return 0

			let aVal: any = aValue
			let bVal: any = bValue

			if (aVal instanceof Date) aVal = aVal.getTime()
			if (bVal instanceof Date) bVal = bVal.getTime()

			const aParsed = typeof aVal === 'string' ? Date.parse(aVal) : NaN
			const bParsed = typeof bVal === 'string' ? Date.parse(bVal) : NaN

			if (!isNaN(aParsed) && !isNaN(bParsed)) {
				aVal = aParsed
				bVal = bParsed
			}

			if (typeof aVal === 'number' && typeof bVal === 'number') {
				return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
			}

			if (typeof aVal === 'string' && typeof bVal === 'string') {
				return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
			}

			return 0
		})
	}, [data, sortColumn, sortDirection])

	const { filteredBookings } = useMemo(() => {
		const parseCountdown = (val: any) => {
			if (val === null) return null
			if (typeof val === 'string' && val.startsWith('+')) {
				return { type: 'plus', value: Number(val) }
			}
			if (Number(val) === 0) {
				return { type: 'zero', value: 0 }
			}
			return { type: 'normal', value: Number(val) }
		}

		const getSortRank = (status: string, value: any) => {
			if (value === null) return 1 // nulls = near bottom
			if (status === 'done' && value.value <= 0) return 2 // last
			return 0 // normal
		}

		let filtered =
			sortedData?.filter(curr => {
				if (!searchValue) return true

				const searchLowerCase = searchValue.toLowerCase()
				return (
					curr?.user?.id?.toString().includes(searchLowerCase) ||
					curr?.stand_id?.toString().includes(searchLowerCase) ||
					curr?.collect_product_by?.toLowerCase().includes(searchLowerCase) ||
					curr?.collection_status?.toLowerCase().includes(searchLowerCase)
				)
			}) ?? []

		if (sortType === 'asc' || sortType === 'desc') {
			filtered = [...filtered].sort((a, b) => {
				const valA = parseCountdown(a.countdown)
				const valB = parseCountdown(b.countdown)

				const rankA = getSortRank(a.collection_status, valA)
				const rankB = getSortRank(b.collection_status, valB)

				// Always push bottom-ranked items to the end
				if (rankA !== rankB) return rankA - rankB

				// If both null
				if (valA === null && valB === null) return 0
				if (valA === null) return 1
				if (valB === null) return -1

				// Ascending sort
				if (sortType === 'asc') {
					// First, plus values come first, sorted high -> low
					if (valA.type === 'plus' && valB.type !== 'plus') return -1
					if (valA.type !== 'plus' && valB.type === 'plus') return 1

					if (valA.type === 'plus' && valB.type === 'plus') {
						return valB.value - valA.value // Descending inside plus
					}

					// Normal values sorted low -> high
					return valA.value - valB.value
				}

				// Descending sort
				if (sortType === 'desc') {
					// First, normal values come first, sorted high -> low
					if (valA.type === 'plus' && valB.type !== 'plus') return 1
					if (valA.type !== 'plus' && valB.type === 'plus') return -1

					if (valA.type === 'plus' && valB.type === 'plus') {
						return valA.value - valB.value // Ascending inside plus
					}

					// Normal values sorted high -> low
					return valB.value - valA.value
				}

				return 0
			})
		}

		return {
			filteredBookings: filtered,
		}
	}, [sortedData, searchValue, sortType])

	const handleSort = (column: string) => {
		if (sortColumn === column) {
			setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setSortColumn(column)
			setSortDirection('asc')
		}
	}

	const handleNoteSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		const formData = new FormData(event.currentTarget)
		const formJson = Object.fromEntries((formData as any).entries())
		const note = String(formJson.note ?? '')
		if (isNoteDialogVisible.id) {
			if (isNoteDialogVisible.type === 'booking') {
				updateNote({ note: { booking_notes: note }, id: isNoteDialogVisible.id })
			} else {
				updateNote({ note: { user_notes: note }, id: isNoteDialogVisible.id })
			}
		}
	}

	const handleCollectionStatus = async (id: number, status: string) => {
		updateCollectionStatus(
			{ id, status },
			{
				onSuccess: () => {
					toast.success('Collection status updated successfully')
					refetch()
				},
				onError: () => {
					toast.error('Failed to update collection status. Please try again')
				},
			},
		)
	}

	const toggleRow = (id: number) => {
		setExpandedRows(prev =>
			prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id],
		)
	}

	const calcWorkingDays = (startDate: Date, days: number) => {
		let count = 1
		if (!holidays || !startDate) return

		const workingDate = new Date(startDate)

		while (count < days) {
			workingDate.setDate(workingDate.getDate() + 1)
			if (
				workingDate.getDay() !== 0 &&
				!holidays.data?.find(date => isSameDay(parseISO(date.holiday_date), workingDate))
			) {
				count++
			}
		}
		return workingDate
	}

	const selectDayItem = PACKAGES.find(pkg => pkg.id === Number(form.watch('package')))

	useEffect(() => {
		if (startDate) {
			const endDate = calcWorkingDays(startDate, selectDayItem ? selectDayItem.duration : 0)
			if (endDate) {
				form.setValue('end_date', endDate)
			}
		}
	}, [startDate, selectDayItem])

	const onSubmit = (data: CreateBookingForm) => {
		let finalPrice = 0
		let calculatedDuration = null
		let selectedPackage = null
		let selectedExtra = null

		if (data.package === 'custom') {
			finalPrice = parseFloat(data.rent_price as string)
			calculatedDuration =
				Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1

			if (calculatedDuration <= 0) {
				toast.error('End date must be later than start date.')
				return
			}
		} else {
			selectedPackage = PACKAGES.find(item => item.id === Number(data.package))
			selectedExtra = EXTRAS.find(item => item.id === Number(data.additionalServices))

			if (!selectedPackage) {
				toast.error('Invalid package selected.')
				return
			}

			const extraPrice = selectedExtra ? Number(selectedExtra.price) : 0

			finalPrice = Number(selectedPackage.price) + extraPrice
		}

		if (data.userType === 'new') {
			verifyEmail(
				{ email: data.email },
				{
					onSuccess: res => {
						if (res?.data?.message === 'User exist with this email') {
							toast.error('User with this email already exists!')
							return
						} else {
							const passwordString = generateRandomPassword(10)

							const payload = {
								...data,
								iban: normalizeIban(data.iban),
								first_name: data.firstName,
								last_name: data.lastName,
								status: 'active',
								password: passwordString,
								confirm_password: passwordString,
								news_offer: true,
							}

							registerUser(payload, {
								onSuccess: res => {
									if (res?.data?.status) {
										const bookingPayload = {
											shelf_type: 1,
											stand_id: Number(data.shelf),
											duration:
												data.package === 'custom' ? calculatedDuration : selectDayItem?.duration,
											start: format(data.start_date, 'yyyy-MM-dd'),
											end: format(data.end_date, 'yyyy-MM-dd'),
											collect_product_by: selectedExtra?.title,
											rebook: 0,
											rent_price: finalPrice,
											original_price: finalPrice,
											status: 'active',
											prepare: format(subDays(data.start_date, 1), 'yyyy-MM-dd', { locale: enUS }),
											user_id: res?.data?.id,
											description: ' ',
											coupon: 'null',
											referral_code: null,
										}

										placeOrder(bookingPayload, {
											onSuccess: () => {
												sendEmail(
													data.package === 'custom' ? 'Custom' : selectDayItem!,
													selectedExtra!,
													data,
													res?.data,
												)
											},
											onError: (error: any) => {
												toast.error(
													error?.response?.data?.message ?? error?.request ?? error?.message,
												)
											},
										})
									}
								},
							})
						}
					},
				},
			)
		} else {
			const bookingPayload = {
				shelf_type: 1,
				stand_id: Number(data.shelf),
				duration: data.package === 'custom' ? calculatedDuration : selectDayItem?.duration,
				start: format(data.start_date, 'yyyy-MM-dd'),
				end: format(data.end_date, 'yyyy-MM-dd'),
				collect_product_by: selectedExtra?.title,
				rent_price: finalPrice,
				original_price: finalPrice,
				status: 'active',
				prepare: format(subDays(data.start_date, 1), 'yyyy-MM-dd', { locale: enUS }),
				user_id: Number(data.existingUser),
				description: ' ',
				coupon: 'null',
				referral_code: null,
			}
			placeOrder(bookingPayload, {
				onSuccess: () => {
					sendEmail(
						data.package === 'custom' ? 'Custom' : selectDayItem!,
						selectedExtra!,
						data,
						users?.find(user => user.id === Number(data.existingUser)) as User,
					)
				},
				onError: (error: any) => {
					toast.error(error?.response?.data?.message ?? error?.request ?? error?.message)
				},
			})
		}
	}

	const sendEmail = async (
		selectDayItem: (typeof PACKAGES)[0] | string,
		extraItem: (typeof EXTRAS)[0],
		values: CreateBookingForm,
		data: User,
	) => {
		const payload = {
			bookingStand: typeof selectDayItem === 'string' ? selectDayItem : selectDayItem?.title,
			userDetails: data,
			selectedStartDate: values.start_date,
			selectedEndDate: values.end_date,
			selectedPackage: selectDayItem,
			extraItem: extraItem,
			selectedDayItem: selectDayItem,
			selectedStand: standList?.find(stand => stand.id === Number(values.shelf)),
			newsOffer: true,
		}

		sendBookingEmail(payload, {
			onSuccess: () => {
				toast.success('Booking has been created and confirmation email has been sent!')
				refetch()
				form.reset()
				setIsCreateDialogVisible(false)
			},
			onError: () => {
				toast.error('Booking has been created but confirmation email could not be sent!')
			},
		})
	}

	const columns: ColumnDef<Collection>[] = [
		{
			accessorKey: 'id',
			meta: {
				title: 'Buchungs ID',
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="Buchungs-ID" />,
			cell: ({ row }) => {
				const createdDate = new Date(row.original.createdAt)
				const isAfter8March = isAfter(
					createdDate.setHours(0, 0, 0, 0),
					new Date('2026-03-08').setHours(0, 0, 0, 0),
				)

				return (
					<div className="flex">
						<div
							className={cn('bg-yellow-200/80 p-1.5 rounded-[18px] w-fit px-3', {
								'bg-purple-200/80': isAfter8March,
							})}>
							{row.getValue('id')}
						</div>
						<div
							onClick={() =>
								setIsNoteDialogVisible({
									note: row.original.booking_notes ?? '',
									type: 'booking',
									id: row.getValue('id'),
								})
							}
							className="cursor-pointer relative rounded-[18px] w-fit pl-1">
							<MessageCircleMoreIcon className="size-4" />
							<div
								className={cn('size-2 rounded-full bg-red-500 absolute top-0 -right-1', {
									hidden: row.original.booking_notes === null || row.original.booking_notes === '',
								})}
							/>
						</div>
					</div>
				)
			},
		},
		{
			accessorKey: 'stand_id',
			meta: {
				title: 'Regale-ID',
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="Regale-ID" />,
		},
		{
			accessorKey: 'user_id',
			meta: {
				title: 'User ID',
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="User ID" />,
			cell: ({ row }) => (
				<div className="flex items-center">
					<div
						onClick={() => handleSalesRecordDialogOpen(row.original)}
						className="cursor-pointer rounded-[18px] w-fit pr-1">
						<MessageCirclePlusIcon className="size-4" />
					</div>
					<div
						onClick={() => handleSalesRecordHistoryDialogOpen(row.original)}
						className="cursor-pointer rounded-[18px] w-fit pr-1">
						<Eye className="size-4" />
					</div>
					<div
						onClick={() => setIsUserDetailDialogVisible(row.original.user)}
						className="bg-green-200/80 cursor-pointer p-1.5 rounded-[18px] w-fit px-3">
						{row.getValue('user_id')}
					</div>
					<div
						onClick={() =>
							setIsNoteDialogVisible({
								note: row.original.user_notes ?? '',
								type: 'user',
								id: row.getValue('id'),
							})
						}
						className="cursor-pointer relative rounded-[18px] w-fit pl-1">
						<MessageCircleMoreIcon className="size-4" />
						<div
							className={cn('size-2 rounded-full bg-red-500 absolute top-0 -right-1', {
								hidden: row.original.user_notes === null || row.original.user_notes === '',
							})}
						/>
					</div>
				</div>
			),
		},
		{
			accessorKey: 'duration',
			meta: {
				title: 'Dauer',
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="Dauer" />,
		},
		{
			accessorKey: 'collect_product_by',
			meta: {
				title: 'Paket',
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="Paket" />,
		},
		{
			accessorKey: 'coupon.code',
			meta: {
				title: 'Gutschein',
			},
			accessorFn: (row: Booking) => row.coupon?.code,
			header: ({ column }) => <DataTableColumnHeader column={column} title="Gutschein" />,
		},
		{
			accessorKey: 'start',
			meta: {
				title: 'Anfangsdatum',
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="Anfangsdatum" />,
			cell: ({ row }) => (
				<div>{format(toZonedTime(new Date(row.getValue('start')), 'UTC'), 'dd-MM-yyyy')}</div>
			),
		},
		{
			accessorKey: 'end',
			meta: {
				title: 'Enddatum',
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="Enddatum" />,
			cell: ({ row }) => (
				<div>{format(toZonedTime(new Date(row.getValue('end')), 'UTC'), 'dd-MM-yyyy')}</div>
			),
		},
		{
			accessorKey: 'rent_price',
			meta: {
				title: 'Preis',
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="Preis" />,
		},
		{
			accessorKey: 'collection_status',
			meta: {
				title: 'Status',
			},
			accessorFn: (row: Booking) =>
				row.collection_status?.charAt(0)?.toUpperCase() + row.collection_status?.slice(1),
			header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
			cell: ({ row }) => (
				<div
					className={cn(
						'w-fit rounded-[18px] p-1.5 px-3',
						row.getValue('collection_status') === 'Collect'
							? 'bg-[#B99D611A] text-[#B99461]'
							: 'bg-[#61B9641A] text-[#61B964]',
					)}>
					{row.getValue('collection_status')}
				</div>
			),
		},
		{
			accessorKey: 'collection_date',
			meta: {
				title: 'Abgeholt am',
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="Abgeholt am" />,
			cell: ({ row }) => (
				<div>
					{row.getValue('collection_date')
						? format(new Date(row.getValue('collection_date')), 'dd-MM-yyyy')
						: ''}
				</div>
			),
		},
		{
			accessorKey: 'done_date',
			meta: {
				title: 'Bearbeitet am',
			},
			header: ({ column }) => <DataTableColumnHeader column={column} title="Bearbeitet am" />,
			cell: ({ row }) => (
				<div>
					{row.getValue('done_date')
						? format(new Date(row.getValue('done_date')), 'dd-MM-yyyy')
						: ''}
				</div>
			),
		},
		{
			accessorKey: 'countdown',
			meta: {
				title: 'Fällig in',
			},
			header: ({ column }) => (
				<DataTableColumnHeader
					customSortType={sortType}
					setCustomSortType={value => setSortType(value)}
					column={column}
					title="Fällig in"
				/>
			),
			cell: ({ row }) => (
				<div
					className={cn(
						Number(row.getValue('countdown')) > 0
							? 'bg-[#61B964] p-1.5 px-4 w-fit rounded-[18px] h-6 flex items-center justify-center text-white text-xs font-semibold'
							: 'text-secondary text-sm font-semibold',
					)}>
					{Number(row.getValue('countdown')) > 0 ? row.getValue('countdown') : '-'}
				</div>
			),
		},
		{
			id: 'action-collection',
			enableSorting: false,
			enableHiding: false,
			header: ({ column }) => <DataTableColumnHeader column={column} title="Aktionen" />,
			cell: ({ row }) => (
				<div className="flex items-center justify-center gap-0.5">
					<Tooltip>
						<TooltipTrigger
							onClick={() =>
								handleCollectionStatus(
									row.original.id,
									row.original.collection_status === 'collect' ? 'done' : 'collect',
								)
							}
							className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-[33px] flex items-center justify-center rounded-full">
							<EditIcon className="shrink-0" />
						</TooltipTrigger>
						<TooltipContent side="bottom">
							<p className="text-xs font-medium">
								{row.original.collection_status === 'collect' ? 'Erledigt' : 'Wiederherstellen'}
							</p>
						</TooltipContent>
					</Tooltip>
				</div>
			),
		},
	]

	const MobileCollectionsTable = () => {
		return (
			<Table>
				<TableHeader>
					<TableRow className="bg-[#F7F8F880] border-b-0! py-[13px] px-4 rounded-lg">
						<TableHead
							align="center"
							onClick={() => handleSort('id')}
							className="text-center cursor-pointer text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Buchungs ID
								{sortColumn === 'id' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('status')}
							className="text-center cursor-pointer text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Status
								{sortColumn === 'status' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('end')}
							className="text-center cursor-pointer text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Enddatum
								{sortColumn === 'end' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{filteredBookings?.map((booking, index) => (
						<React.Fragment key={booking.id.toString() + index.toString()}>
							<TableRow
								className={cn(
									'h-14',
									expandedRows.includes(booking.id) ? '!border-b-0' : 'border-b border-[#F4F4F4]',
								)}
								onClick={() => toggleRow(booking.id)}>
								<TableCell
									align="center"
									className={cn('text-xs', {
										'text-muted-foreground': !expandedRows.includes(booking.id),
									})}>
									<div className="flex items-center">
										<ChevronRightIcon
											className={cn(
												'size-3 shrink-0 -mt-0.5',
												expandedRows.includes(booking.id)
													? 'rotate-90 text-secondary'
													: 'text-muted-foreground',
											)}
										/>
										<div
											className={cn('bg-yellow-200/80 px-1 py-0.5 rounded-[18px] w-fit', {
												'bg-purple-200/80': isAfter(
													new Date(booking.createdAt).setHours(0, 0, 0, 0),
													new Date('2026-03-08').setHours(0, 0, 0, 0),
												),
											})}>
											{booking.id}
										</div>
									</div>
								</TableCell>
								<TableCell
									align="center"
									className={cn('text-[10px]', {
										'text-muted-foreground': !expandedRows.includes(booking.id),
									})}>
									<div
										className={cn(
											'text-[10px] capitalize w-fit p-1.5 px-2 rounded-[18px]',
											booking.collection_status === 'collect'
												? 'bg-[#B99D611A] text-[#B99461]'
												: 'bg-[#61B9641A] text-[#61B964]',
										)}>
										{booking.collection_status}
									</div>
								</TableCell>
								<TableCell
									align="center"
									className={cn('text-xs', {
										'text-muted-foreground': !expandedRows.includes(booking.id),
									})}>
									{format(toZonedTime(new Date(booking.end), 'UTC'), 'dd-MM-yyyy')}
								</TableCell>
							</TableRow>
							{expandedRows.includes(booking.id) && (
								<TableRow
									className={cn(
										'mt-2',
										!expandedRows.includes(booking.id)
											? '!border-b-0'
											: 'border-b border-[#F4F4F4]',
									)}>
									<TableCell className="pt-0" colSpan={3}>
										<div className="px-3 flex flex-col gap-4">
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													Buchungshinweise
												</h6>
												<p
													onClick={() =>
														setIsNoteDialogVisible({
															note: booking.booking_notes ?? '',
															type: 'booking',
															id: booking.id,
														})
													}
													className="text-secondary relative bg-yellow-200/80 p-1.5 px-3 rounded-[18px] w-fit cursor-pointer text-xs font-medium">
													<MessageCircleMoreIcon className="size-3" />
													<div
														className={cn(
															'size-1.5 rounded-full bg-red-500 absolute top-1 right-2',
															{
																hidden:
																	booking.booking_notes === null || booking.booking_notes === '',
															},
														)}
													/>
												</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													Startdatum
												</h6>
												<p className="text-secondary text-xs font-medium">
													{format(toZonedTime(new Date(booking.start), 'UTC'), 'dd-MM-yyyy')}
												</p>
											</div>

											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Regale-ID</h6>
												<p className="text-secondary text-xs font-medium">{booking.stand_id}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">User ID</h6>
												<div className="flex items-center gap-2">
													<p
														onClick={() => handleSalesRecordDialogOpen(booking)}
														className="text-secondary bg-green-200/80 p-1.5 px-3 rounded-[18px] w-fit cursor-pointer text-xs font-medium">
														<MessageCirclePlusIcon className="size-3" />
													</p>
													<p
														onClick={() => handleSalesRecordHistoryDialogOpen(booking)}
														className="text-secondary bg-green-200/80 p-1.5 px-3 rounded-[18px] w-fit cursor-pointer text-xs font-medium">
														<Eye className="size-3" />
													</p>
													<p
														onClick={() => setIsUserDetailDialogVisible(booking.user)}
														className="text-secondary bg-green-200/80 p-1.5 px-3 rounded-[18px] w-fit cursor-pointer text-xs font-medium">
														{booking.user_id}
													</p>
													<p
														onClick={() =>
															setIsNoteDialogVisible({
																note: booking.user_notes ?? '',
																type: 'user',
																id: booking.id,
															})
														}
														className="text-secondary relative bg-green-200/80 p-1.5 px-3 rounded-[18px] w-fit cursor-pointer text-xs font-medium">
														<MessageCircleMoreIcon className="size-3" />
														<div
															className={cn(
																'size-1.5 rounded-full bg-red-500 absolute top-1 right-2',
																{
																	hidden: booking.user_notes === null || booking.user_notes === '',
																},
															)}
														/>
													</p>
												</div>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Dauer</h6>
												<p className="text-secondary text-xs font-medium">{booking.duration}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Paket</h6>
												<p className="text-secondary text-xs font-medium">
													{booking.collect_product_by}
												</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Gutschein</h6>
												<p className="text-secondary text-xs font-medium">{booking.coupon?.code}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Preis</h6>
												<p className="text-secondary text-xs font-medium">{booking.rent_price}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													Abgeholt am
												</h6>
												{booking.collection_date && (
													<p className="text-secondary text-xs font-medium">
														{format(new Date(booking.collection_date), 'dd-MM-yyyy')}
													</p>
												)}
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													Bearbeitet am
												</h6>
												{booking.done_date && (
													<p className="text-secondary text-xs font-medium">
														{format(new Date(booking.done_date), 'dd-MM-yyyy')}
													</p>
												)}
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Fällig in</h6>
												<div
													className={cn(
														'text-[10px] capitalize w-fit p-1.5 px-2 rounded-[18px]',
														Number(booking.countdown) > 0
															? 'bg-[#61B964] h-6 rounded-[18px] p-1.5 w-fit px-3 text-white text-xs font-semibold'
															: 'text-secondary text-sm font-semibold',
													)}>
													{Number(booking.countdown) > 0 ? booking.countdown : '-'}
												</div>
											</div>

											<div className="flex items-center justify-between">
												<div className="flex items-center gap-1">
													<InformationCircleIcon className="text-muted-foreground size-3" />
													<h6 className="text-muted-foreground text-[10px] font-medium">Aktion</h6>
												</div>
												<div className="flex items-center gap-1">
													<div
														onClick={() =>
															handleCollectionStatus(
																booking.id,
																booking.collection_status === 'collect' ? 'done' : 'collect',
															)
														}
														className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-6 flex items-center justify-center rounded-full">
														<EditIcon className="shrink-0 max-lg:size-3" />
													</div>
												</div>
											</div>
										</div>
									</TableCell>
								</TableRow>
							)}
						</React.Fragment>
					))}
				</TableBody>
			</Table>
		)
	}

	return (
		<div className="rounded-[20px] bg-white max-lg:p-4 max-lg:pr-3 lg:px-4 lg:py-6 flex flex-col">
			<SalesRecordsDialog
				dialogState={isSalesRecordDialogVisible}
				onClose={() => setIsSalesRecordDialogVisible(null)}
				onSuccess={() => refetch()}
			/>
			<SalesRecordsHistoryDialog
				dialogState={isSalesRecordHistoryDialogVisible}
				onClose={() => setIsSalesRecordHistoryDialogVisible(null)}
				onSuccess={() => refetch()}
			/>
			<ResponsiveModal
				open={isNoteDialogVisible.id !== null}
				onOpenChange={() => setIsNoteDialogVisible({ note: '', type: null, id: null })}>
				<ResponsiveModalContent className="gap-0 !max-w-[550px]">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							{isNoteDialogVisible.type === 'booking' ? 'Buchungshinweis' : 'Benutzerhinweis'}
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							{isNoteDialogVisible.type === 'booking' ? 'Buchungshinweis' : 'Benutzerhinweis'}
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<form onSubmit={handleNoteSubmit} className="mt-3 lg:mt-4 flex flex-col">
						<Textarea
							defaultValue={isNoteDialogVisible.note}
							className="resize-none max-lg:placeholder:text-sm min-h-[117px]"
							name="note"
							placeholder="Notiz hinzufügen"
						/>
						<Button
							variant="secondary"
							className="mt-6 w-full lg:h-[44px] rounded-[12px] font-extrabold text-white">
							{isUpdatingNote && <Loader2 className="animate-spin" />}
							{isUpdatingNote ? 'Bitte warten' : 'Speichern'}
						</Button>
					</form>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<ResponsiveModal
				open={!!isUserDetailDialogVisible}
				onOpenChange={() => setIsUserDetailDialogVisible(null)}>
				<ResponsiveModalContent className="gap-0 !max-w-[550px]">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							Benutzerdetails
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							Benutzerdetails
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<div className="mt-8 lg:mt-12 flex flex-col gap-4 lg:gap-5">
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">ID</p>
							<p className="text-secondary font-extrabold">{isUserDetailDialogVisible?.id}</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Vorname</p>
							<p className="text-secondary  capitalize font-extrabold">
								{isUserDetailDialogVisible?.first_name}
							</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Nachname</p>
							<p className="text-secondary font-extrabold capitalize">
								{isUserDetailDialogVisible?.last_name}
							</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">E-Mail</p>
							<p className="text-secondary font-extrabold">{isUserDetailDialogVisible?.email}</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Telefon</p>
							<p className="text-secondary font-extrabold">
								{isUserDetailDialogVisible?.phone_number}
							</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Land</p>
							<p className="capitalize text-secondary font-extrabold">
								{isUserDetailDialogVisible?.country}
							</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">PLZ</p>
							<p className="text-secondary font-extrabold">{isUserDetailDialogVisible?.zip}</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Stadt</p>
							<p className="capitalize text-secondary font-extrabold">
								{isUserDetailDialogVisible?.country}
							</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Adresse</p>
							<p className="capitalize text-secondary font-extrabold">
								{isUserDetailDialogVisible?.address}
							</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">IBAN</p>
							<p className="text-secondary font-extrabold">
								{formatIban(isUserDetailDialogVisible?.iban)}
							</p>
						</div>
						<div className="flex items-center justify-between">
							<p className="text-secondary/50 font-medium">Erstellt am</p>
							{isUserDetailDialogVisible && (
								<p className="text-secondary font-extrabold">
									{format(
										toZonedTime(new Date(isUserDetailDialogVisible.createdAt), 'UTC'),
										'dd.MM.yyyy, HH:mm',
									)}{' '}
								</p>
							)}
						</div>
					</div>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<ResponsiveModal
				open={isCreateDialogVisible}
				onOpenChange={() => {
					form.reset()
					setIsCreateDialogVisible(false)
				}}>
				<ResponsiveModalContent className="gap-0">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							Buchung erstellen
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							Create new booking
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)}>
							<div className="mt-3 lg:mt-8">
								<RadioGroup
									value={form.watch('userType')}
									className="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-5"
									onValueChange={value => {
										form.clearErrors()
										form.setValue('userType', value as 'existing' | 'new')
									}}>
									<Label
										htmlFor="r1"
										className={cn(
											'flex border cursor-pointer rounded-2xl text-sm font-medium py-[18px] px-4 items-center space-x-1',
											form.watch('userType') === 'new'
												? 'border-primary text-primary'
												: 'border-muted-foreground text-muted-foreground',
										)}>
										<RadioGroupItem value="new" id="r1" />
										<p>Neuer Nutzer</p>
									</Label>
									<Label
										htmlFor="r2"
										className={cn(
											'flex border cursor-pointer rounded-2xl text-sm font-medium py-[18px] px-4 items-center space-x-1',
											form.watch('userType') === 'existing'
												? 'border-primary text-primary'
												: 'border-muted-foreground text-muted-foreground',
										)}>
										<RadioGroupItem value="existing" id="r2" />
										<p>Bestehender Nutzer</p>
									</Label>
								</RadioGroup>
							</div>
							{form.watch('userType') === 'new' && (
								<div className="mt-6 lg:mt-8">
									<h2 className="text-muted-foreground text-sm lg:text-xl font-medium">
										Benutzerdetails
									</h2>
									<div className="grid grid-cols-1 lg:grid-cols-2 mt-6 gap-y-6 lg:mt-10 lg:gap-x-4 lg:gap-y-5">
										<FormField
											control={form.control}
											name="firstName"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														First Name <span className="text-destructive-foreground">*</span>
													</FormLabel>
													<FormControl>
														<Input
															placeholder="First Name"
															{...field}
															value={capitalizeFirstLetter(field.value)}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="lastName"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														Last Name <span className="text-destructive-foreground">*</span>
													</FormLabel>
													<FormControl>
														<Input
															placeholder="Last Name"
															{...field}
															value={capitalizeFirstLetter(field.value)}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="email"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														Email <span className="text-destructive-foreground">*</span>
													</FormLabel>
													<FormControl>
														<Input placeholder="Email" {...field} />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="country"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														Ort <span className="text-destructive-foreground">*</span>
													</FormLabel>
													<FormControl>
														<Input placeholder="Ort" {...field} />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="zip"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														Postleitzahl <span className="text-destructive-foreground">*</span>
													</FormLabel>
													<FormControl>
														<Input placeholder="Postleitzahl" {...field} />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="iban"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														IBAN <span className="text-destructive-foreground">*</span>
													</FormLabel>
													<FormControl>
														<InputMask
															{...primaryIbanOptions}
															value={field.value ?? ''}
															onChange={e => field.onChange(e.target.value)}
															placeholder="IBAN"
															component={Input}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="address"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														Adresse <span className="text-destructive-foreground">*</span>
													</FormLabel>
													<FormControl>
														<Input
															placeholder="Adresse"
															{...field}
															value={capitalizeFirstLetter(field.value)}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="phoneNumber"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														Telefonnummer <span className="text-destructive-foreground">*</span>
													</FormLabel>
													<FormControl>
														<Input placeholder="Telefonnummer" {...field} />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<h2 className="text-muted-foreground mt-10 lg:mt-16 text-xs lg:text-xl font-medium">
										Buchungsdetails
									</h2>
									<div className="grid grid-cols-1 lg:grid-cols-2 mt-6 lg:mt-10 lg:gap-x-4 gap-y-6 lg:gap-y-5">
										<FormField
											control={form.control}
											name="package"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														Paket <span className="text-destructive-foreground">*</span>
													</FormLabel>
													<Select
														onValueChange={value => {
															form.setValue('start_date', null as any)
															form.setValue('end_date', null as any)
															field.onChange(value)
														}}
														value={field.value}>
														<FormControl>
															<SelectTrigger>
																<SelectValue placeholder="Paket" />
															</SelectTrigger>
														</FormControl>
														<SelectContent>
															{PACKAGES.map(item => (
																<SelectItem key={item.id} value={item.id.toString()}>
																	{item.title} (€ {item.price})
																</SelectItem>
															))}
															<SelectItem value="custom">Benutzerdefiniert</SelectItem>
														</SelectContent>
													</Select>
													<FormMessage />
												</FormItem>
											)}
										/>
										{form.watch('package') && (
											<FormField
												control={form.control}
												name="start_date"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">
															Startdatum <span className="text-destructive-foreground">*</span>
														</FormLabel>
														<Popover open={openStartCalendar} onOpenChange={setOpenStartCalendar}>
															<PopoverTrigger asChild>
																<FormControl>
																	<Button
																		variant={'outline'}
																		className={cn(
																			'w-full max-lg:text-xs hover:bg-white px-4 py-[18px] h-10 lg:h-14 rounded-[12px] justify-between border-input focus-visible:border-ring focus-visible:ring-ring/50 focus:!border-3 focus:!border-secondary/50 focus-visible:ring-[3px] text-left font-normal',
																			!field.value && 'text-muted-foreground',
																		)}>
																		{field.value ? (
																			format(field.value, 'dd.MM.yyyy')
																		) : (
																			<span>dd.mm.yyyy</span>
																		)}
																		<ChevronDownIcon className="size-4 opacity-50" />
																	</Button>
																</FormControl>
															</PopoverTrigger>
															<PopoverContent className="p-0 border-input" align="start">
																<Calendar
																	mode="single"
																	onSelect={val => {
																		field.onChange(val)
																		setOpenStartCalendar(false)
																	}}
																	selected={field.value}
																	initialFocus
																/>
															</PopoverContent>
														</Popover>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}
										{form.watch('package') && (
											<FormField
												control={form.control}
												name="end_date"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">
															Enddatum <span className="text-destructive-foreground">*</span>
														</FormLabel>
														<Popover open={openEndCalendar} onOpenChange={setOpenEndCalendar}>
															<PopoverTrigger asChild>
																<FormControl>
																	<Button
																		variant={'outline'}
																		disabled={form.watch('package') !== 'custom'}
																		className={cn(
																			'w-full hover:bg-white max-lg:text-xs px-4 py-[18px] h-10 lg:h-14 rounded-[12px] justify-between border-input focus-visible:border-ring focus-visible:ring-ring/50 focus:!border-3 focus:!border-secondary/50 focus-visible:ring-[3px] text-left font-normal',
																			!field.value && 'text-muted-foreground',
																		)}>
																		{field.value ? (
																			format(field.value, 'dd.MM.yyyy')
																		) : (
																			<span>dd.mm.yyyy</span>
																		)}
																		<ChevronDownIcon className="size-4 opacity-50" />
																	</Button>
																</FormControl>
															</PopoverTrigger>
															<PopoverContent className="w-auto p-0 border-input" align="start">
																<Calendar
																	mode="single"
																	selected={field.value}
																	onSelect={val => {
																		field.onChange(val)
																		setOpenEndCalendar(false)
																	}}
																	disabled={date => date < form.watch('start_date')}
																	initialFocus
																/>
															</PopoverContent>
														</Popover>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}
										{startDate && endDate && (
											<FormField
												control={form.control}
												name="shelf"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">
															Regal Nummer <span className="text-destructive-foreground">*</span>
														</FormLabel>
														<Select onValueChange={field.onChange} value={field.value}>
															<FormControl>
																<SelectTrigger>
																	<SelectValue placeholder="Paket" />
																</SelectTrigger>
															</FormControl>
															<SelectContent>
																{standList &&
																	standList.length > 0 &&
																	standList
																		.filter(
																			stand => stand.is_available && stand.status === 'active',
																		)
																		.map(stand => (
																			<SelectItem
																				key={'stand_' + stand.id}
																				value={stand.id.toString()}>
																				{stand.label}
																			</SelectItem>
																		))}
															</SelectContent>
														</Select>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}
										{form.watch('package') === 'custom' && (
											<FormField
												control={form.control}
												name="rent_price"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">
															Preis <span className="text-destructive-foreground">*</span>
														</FormLabel>
														<FormControl>
															<Input
																placeholder="Preis"
																inputMode="numeric"
																{...field}
																onChange={e => {
																	const value = e.target.value
																	if (/^\d*\.?\d*$/.test(value)) {
																		field.onChange(value)
																	}
																}}
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}
										{form.watch('shelf') && (
											<FormField
												control={form.control}
												name="additionalServices"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">
															Zusatzleistungen{' '}
															<span className="text-destructive-foreground">*</span>
														</FormLabel>
														<Select onValueChange={field.onChange} value={field.value}>
															<FormControl>
																<SelectTrigger>
																	<SelectValue placeholder="Paket" />
																</SelectTrigger>
															</FormControl>
															<SelectContent>
																{EXTRAS.map(extra => (
																	<SelectItem key={extra.id} value={extra.id.toString()}>
																		{extra.title} (€ {extra.price})
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}
									</div>
								</div>
							)}
							{form.watch('userType') === 'existing' && (
								<div className="mt-6 lg:mt-8">
									<h2 className="text-muted-foreground text-sm lg:text-xl font-medium">
										Benutzerdetails
									</h2>
									<div className="mt-6 lg:mt-10 ">
										<FormField
											control={form.control}
											name="existingUser"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														Benutzer auswählen{' '}
														<span className="text-destructive-foreground">*</span>
													</FormLabel>
													<Popover open={open} onOpenChange={setOpen}>
														<PopoverTrigger asChild>
															<FormControl>
																<Button
																	variant="outline"
																	role="combobox"
																	className={cn(
																		'hover:bg-white overflow-hidden text-ellipsis max-lg:text-xs px-4 py-[18px] h-10 lg:h-14 rounded-[12px] justify-between border-input focus-visible:border-ring focus-visible:ring-ring/50 focus:!border-3 focus:!border-secondary/50 focus-visible:ring-[3px] text-left font-normal',
																		!field.value && 'text-muted-foreground',
																	)}>
																	<p className="max-w-11/12 overflow-hidden text-ellipsis">
																		{field.value
																			? `ID ${users?.find(user => user.id === Number(field.value))?.id} ${capitalizeFirstLetter(users?.find(user => user.id === Number(field.value))?.first_name ?? '')} ${capitalizeFirstLetter(users?.find(user => user.id === Number(field.value))?.last_name ?? '')} (${users?.find(user => user.id === Number(field.value))?.email})`
																			: 'Benutzer auswählen'}
																	</p>

																	<ChevronDownIcon className="shrink-0 size-4 opacity-50" />
																</Button>
															</FormControl>
														</PopoverTrigger>
														<PopoverContent className="w-[200px] p-0 border-input">
															<Command>
																<CommandInput placeholder="Benutzer suchen..." className="h-9" />
																<CommandList>
																	<CommandEmpty>Kein Benutzer gefunden.</CommandEmpty>
																	<CommandGroup>
																		{Array.isArray(users) &&
																			users?.map(user => (
																				<CommandItem
																					value={`ID ${user.id} ${user.first_name} ${user.last_name} (
                                            ${user.email})`}
																					key={user.id}
																					onSelect={() => {
																						form.setValue('existingUser', user.id.toString(), {
																							shouldValidate: true,
																						})
																						setOpen(false)
																					}}>
																					ID {user.id} {capitalizeFirstLetter(user.first_name)}{' '}
																					{capitalizeFirstLetter(user.last_name)} ({user.email})
																					<Check
																						className={cn(
																							'ml-auto',
																							user.id.toString() === field.value
																								? 'opacity-100'
																								: 'opacity-0',
																						)}
																					/>
																				</CommandItem>
																			))}
																	</CommandGroup>
																</CommandList>
															</Command>
														</PopoverContent>
													</Popover>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<h2 className="text-muted-foreground mt-10 lg:mt-16 text-xs lg:text-xl font-medium">
										Buchungsdetails
									</h2>
									<div className="grid grid-cols-1 lg:grid-cols-2 mt-6 lg:mt-10 lg:gap-x-4 gap-y-6 lg:gap-y-5">
										<FormField
											control={form.control}
											name="package"
											render={({ field }) => (
												<FormItem className="w-full">
													<FormLabel className="text-muted-foreground">
														Paket <span className="text-destructive-foreground">*</span>
													</FormLabel>
													<Select
														onValueChange={value => {
															form.setValue('start_date', null as any)
															form.setValue('end_date', null as any)
															field.onChange(value)
														}}
														value={field.value}>
														<FormControl>
															<SelectTrigger>
																<SelectValue placeholder="Paket" />
															</SelectTrigger>
														</FormControl>
														<SelectContent>
															{PACKAGES.map(item => (
																<SelectItem key={item.id} value={item.id.toString()}>
																	{item.title} (€ {item.price})
																</SelectItem>
															))}
															<SelectItem value="custom">Benutzerdefiniert</SelectItem>
														</SelectContent>
													</Select>
													<FormMessage />
												</FormItem>
											)}
										/>
										{form.watch('package') && (
											<FormField
												control={form.control}
												name="start_date"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">
															Startdatum <span className="text-destructive-foreground">*</span>
														</FormLabel>
														<Popover open={openStartCalendar} onOpenChange={setOpenStartCalendar}>
															<PopoverTrigger asChild>
																<FormControl>
																	<Button
																		variant={'outline'}
																		className={cn(
																			'w-full max-lg:text-xs hover:bg-white px-4 py-[18px] h-10 lg:h-14 rounded-[12px] justify-between border-input focus-visible:border-ring focus-visible:ring-ring/50 focus:!border-3 focus:!border-secondary/50 focus-visible:ring-[3px] text-left font-normal',
																			!field.value && 'text-muted-foreground',
																		)}>
																		{field.value ? (
																			format(field.value, 'dd.MM.yyyy')
																		) : (
																			<span>dd.mm.yyyy</span>
																		)}
																		<ChevronDownIcon className="size-4 opacity-50" />
																	</Button>
																</FormControl>
															</PopoverTrigger>
															<PopoverContent className="p-0 border-input" align="start">
																<Calendar
																	mode="single"
																	onSelect={val => {
																		field.onChange(val)
																		setOpenStartCalendar(false)
																	}}
																	selected={field.value}
																	initialFocus
																/>
															</PopoverContent>
														</Popover>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}
										{form.watch('package') && (
											<FormField
												control={form.control}
												name="end_date"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">
															Enddatum <span className="text-destructive-foreground">*</span>
														</FormLabel>
														<Popover open={openEndCalendar} onOpenChange={setOpenEndCalendar}>
															<PopoverTrigger asChild>
																<FormControl>
																	<Button
																		variant={'outline'}
																		disabled={form.watch('package') !== 'custom'}
																		className={cn(
																			'w-full hover:bg-white max-lg:text-xs px-4 py-[18px] h-10 lg:h-14 rounded-[12px] justify-between border-input focus-visible:border-ring focus-visible:ring-ring/50 focus:!border-3 focus:!border-secondary/50 focus-visible:ring-[3px] text-left font-normal',
																			!field.value && 'text-muted-foreground',
																		)}>
																		{field.value ? (
																			format(field.value, 'dd.MM.yyyy')
																		) : (
																			<span>dd.mm.yyyy</span>
																		)}
																		<ChevronDownIcon className="size-4 opacity-50" />
																	</Button>
																</FormControl>
															</PopoverTrigger>
															<PopoverContent className="w-auto p-0 border-input" align="start">
																<Calendar
																	mode="single"
																	selected={field.value}
																	onSelect={val => {
																		field.onChange(val)
																		setOpenEndCalendar(false)
																	}}
																	disabled={date => date < form.watch('start_date')}
																	initialFocus
																/>
															</PopoverContent>
														</Popover>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}
										{startDate && endDate && (
											<FormField
												control={form.control}
												name="shelf"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">
															Regal Nummer <span className="text-destructive-foreground">*</span>
														</FormLabel>
														<Select onValueChange={field.onChange} value={field.value}>
															<FormControl>
																<SelectTrigger>
																	<SelectValue placeholder="Paket" />
																</SelectTrigger>
															</FormControl>
															<SelectContent>
																{standList &&
																	standList.length > 0 &&
																	standList
																		.filter(
																			stand => stand.is_available && stand.status === 'active',
																		)
																		.map(stand => (
																			<SelectItem
																				key={'stand_' + stand.id}
																				value={stand.id.toString()}>
																				{stand.label}
																			</SelectItem>
																		))}
															</SelectContent>
														</Select>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}
										{form.watch('package') === 'custom' && (
											<FormField
												control={form.control}
												name="rent_price"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">
															Preis <span className="text-destructive-foreground">*</span>
														</FormLabel>
														<FormControl>
															<Input
																placeholder="Preis"
																{...field}
																onChange={e => {
																	const value = e.target.value
																	if (/^\d*\.?\d*$/.test(value)) {
																		field.onChange(value)
																	}
																}}
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}
										{form.watch('shelf') && (
											<FormField
												control={form.control}
												name="additionalServices"
												render={({ field }) => (
													<FormItem className="w-full">
														<FormLabel className="text-muted-foreground">
															Zusatzleistungen{' '}
															<span className="text-destructive-foreground">*</span>
														</FormLabel>
														<Select onValueChange={field.onChange} value={field.value}>
															<FormControl>
																<SelectTrigger>
																	<SelectValue placeholder="Paket" />
																</SelectTrigger>
															</FormControl>
															<SelectContent>
																{EXTRAS.map(extra => (
																	<SelectItem key={extra.id} value={extra.id.toString()}>
																		{extra.title} (€ {extra.price})
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}
									</div>
								</div>
							)}
							<Button
								variant="secondary"
								className="mt-20 w-full lg:h-[61px] rounded-[12px] font-extrabold text-white">
								{placingBooking && <Loader2 className="animate-spin" />}
								{placingBooking ? 'Bitte warten' : 'Speichern'}
							</Button>
						</form>
					</Form>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<div className="lg:hidden rounded-xl border border-[#EEEFF0] size-10 flex items-center justify-center">
						<TaskSquareIcon className="text-secondary" />
					</div>
					<h1 className="text-muted-foreground lg:text-secondary text-xs lg:text-2xl font-medium lg:font-extrabold">
						Mietende Liste
					</h1>
				</div>
				<Button
					size="icon"
					variant="secondary"
					onClick={() => setIsCreateDialogVisible(true)}
					className="size-8 lg:size-14 rounded-lg lg:rounded-xl bg-secondary">
					<PlusIcon className="size-5 lg:size-6 text-white" />
				</Button>
			</div>
			<div className="flex max-lg:mt-5 mt-6 flex-col gap-3 lg:gap-4">
				<div className="grid grid-cols-2 items-center gap-[9px] lg:hidden">
					<div className="relative flex items-center">
						<Input
							value={searchValue}
							onChange={e => setSearchValue(e.target.value)}
							className="!pl-[34px] h-10 text-xs"
							placeholder="Suche..."
						/>
						<MagnifyingGlassIcon className="size-4 text-muted-foreground absolute left-2" />
					</div>
					<Select
						value={rowsPerPage.toString()}
						onValueChange={value => setRowsPerPage(Number(value))}>
						<SelectTrigger className="w-full h-10 text-xs">
							<SelectValue placeholder="zeigen 50" />
						</SelectTrigger>
						<SelectContent>
							{[50, 80, 100, 200, 500].map(pageSize => (
								<SelectItem key={pageSize} value={pageSize.toString()}>
									zeigen {pageSize}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="lg:hidden flex flex-col gap-3">
					<Select value={selectedStatus} onValueChange={setSelectedStatus}>
						<SelectTrigger className="w-full h-10 text-xs">
							<SelectValue placeholder="Collection Status" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All</SelectItem>
							<SelectItem value="collect">Collect</SelectItem>
							<SelectItem value="done">Done</SelectItem>
						</SelectContent>
					</Select>
					<Select value={selectedColumn} onValueChange={setSelectedColumn}>
						<SelectTrigger className="h-10 text-xs w-full">
							<SelectValue placeholder="Kategorie" />
						</SelectTrigger>
						<SelectContent>
							{searchableColumns.map(column => (
								<SelectItem key={column.value} value={column.value}>
									{column.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<div className="flex lg:hidden items-center gap-2">
						<Input
							value={filterValue}
							onChange={e => setFilterValue(e.target.value)}
							className="h-10 text-xs"
							placeholder="Bezeichnung eingeben"
						/>
						<Button
							onClick={() => refetch()}
							variant="secondary"
							className="w-[73px] py-3 text-xs font-medium bg-secondary rounded-lg">
							Suche
						</Button>
					</div>
				</div>
				<div className="flex max-lg:hidden items-center gap-3">
					<Select value={selectedColumn} onValueChange={setSelectedColumn}>
						<SelectTrigger className="flex-1">
							<SelectValue placeholder="Kategorie" />
						</SelectTrigger>
						<SelectContent>
							{searchableColumns.map(column => (
								<SelectItem key={column.value} value={column.value}>
									{column.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Input
						value={filterValue}
						onChange={e => setFilterValue(e.target.value)}
						className="flex-1"
						placeholder="Bezeichnung eingeben"
					/>
					<Button
						onClick={() => refetch()}
						variant="secondary"
						className="w-[90px] font-medium bg-secondary rounded-xl">
						Suche
					</Button>
				</div>
			</div>
			<div className="flex max-lg:hidden items-center justify-between mt-6">
				<div className="flex items-center gap-4">
					<Select
						value={rowsPerPage.toString()}
						onValueChange={value => setRowsPerPage(Number(value))}>
						<SelectTrigger className="w-[127px]">
							<SelectValue placeholder="zeigen 50" />
						</SelectTrigger>
						<SelectContent>
							{[50, 80, 100, 200, 500].map(pageSize => (
								<SelectItem key={pageSize} value={pageSize.toString()}>
									zeigen {pageSize}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="flex items-center basis-1/2 gap-8">
					<PaginationComponent
						currentPage={page}
						totalPages={data?.pagination?.totalPages ?? 1}
						onPageChange={setPage}
					/>
					<div className="relative flex flex-1 items-center">
						<Input
							value={searchValue}
							onChange={e => setSearchValue(e.target.value)}
							className="!pl-[38px]"
							placeholder="Suche..."
						/>
						<MagnifyingGlassIcon className="size-5 text-muted-foreground absolute left-2" />
					</div>
					<Select value={selectedStatus} onValueChange={setSelectedStatus}>
						<SelectTrigger className="w-full h-10 text-xs flex-1">
							<SelectValue placeholder="Collection Status" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All</SelectItem>
							<SelectItem value="collect">Collect</SelectItem>
							<SelectItem value="done">Done</SelectItem>
						</SelectContent>
					</Select>
				</div>
			</div>
			<div className="mt-4">
				{isSmallScreen ? (
					<MobileCollectionsTable />
				) : (
					<DataTable
						columns={columns}
						canToggleVisibility
						data={filteredBookings ?? []}
						isLoading={isLoading}
					/>
				)}
			</div>
			<div className="mt-6 lg:mt-16 flex justify-center">
				<PaginationComponent
					currentPage={page}
					totalPages={data?.pagination.totalPages ?? 1}
					onPageChange={setPage}
				/>
			</div>
		</div>
	)
}
